import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Archive, ArrowLeft, Plus, Search, Trash2 } from "lucide-react";

import {
  useAddMaterialListLine,
  useArchiveMaterialList,
  useInventory,
  useMaterialList,
  useRemoveMaterialListLine,
  useUpdateMaterialListLine,
} from "@/api/queries";
import type {
  MaterialListLine,
  MaterialListLineStatus,
  PlanningPriority,
  UnitCode,
} from "@/api/types";
import { Button } from "@/components/Button";
import { Dialog } from "@/components/Dialog";
import { TableShell, Td, Th, Tr } from "@/components/DataTable";
import { Field, Input, Select } from "@/components/Input";
import { EmptyState, ErrorState, LoadingState } from "@/components/States";
import { StatusBadge } from "@/components/StatusBadge";
import { useToast } from "@/components/toast-context";
import { Page } from "@/layout/Page";
import { PageHeader } from "@/layout/PageHeader";
import {
  formatDate,
  formatMaterialListLineStatus,
  formatPlanningPriority,
  formatMaterialListStatus,
  formatMaterialListType,
  formatQuantity,
  formatUnit,
} from "@/lib/format";
import { materialListLineTone, materialListTone } from "@/lib/stock";
import { STOCKABLE_UNITS } from "@/lib/units";

const LINE_STATUSES: MaterialListLineStatus[] = [
  "TO_GET",
  "FOUND",
  "PURCHASED",
  "CANCELLED",
];
const PRIORITIES: PlanningPriority[] = ["LOW", "NORMAL", "HIGH", "URGENT"];

/**
 * One list, and its lines (V1.6-01).
 *
 * The screen exists to make one thing easy: writing down something the
 * catalogue does not have. A free-text line is not a degraded case here, it is
 * the normal one — « boîte inox spéciale environ 12x12 » goes in as typed.
 */
export function MaterialListDetailPage() {
  const { listId = "" } = useParams();
  const toast = useToast();
  const { data, isLoading, error, refetch } = useMaterialList(listId);
  const archive = useArchiveMaterialList();
  const [adding, setAdding] = useState(false);

  if (isLoading) return <LoadingState label="Ouverture de la liste…" />;
  if (error) return <ErrorState error={error} onRetry={() => void refetch()} />;
  if (!data) return null;

  const { material_list: list, lines, counts } = data;
  const closed = list.status === "ARCHIVED" || list.status === "COMPLETED";

  async function archiveList() {
    try {
      await archive.mutateAsync({ listId });
      toast.success("Liste archivée", "Les lignes restent lisibles.");
    } catch (archiveError) {
      toast.error("Liste non archivée", String(archiveError));
    }
  }

  return (
    <Page>
      <PageHeader
        title={list.name}
        subtitle={`${list.code} · ${formatMaterialListType(list.list_type)}`}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <Link
              to="/lists"
              className="hidden items-center gap-1.5 rounded-md px-2 py-2 text-[13px] text-ink-subtle hover:text-ink md:flex"
            >
              <ArrowLeft className="size-4" aria-hidden />
              Listes
            </Link>
            {!closed && (
              <Button
                variant="primary"
                size="sm"
                onClick={() => setAdding(true)}
              >
                <Plus className="size-4" aria-hidden />
                Ajouter
              </Button>
            )}
            {list.status !== "ARCHIVED" && (
              <Button
                variant="secondary"
                size="sm"
                onClick={() => void archiveList()}
                loading={archive.isPending}
              >
                <Archive className="size-4" aria-hidden />
                Archiver
              </Button>
            )}
          </div>
        }
      />

      <div className="flex flex-wrap items-center gap-2 text-[13px] text-ink-muted">
        <StatusBadge tone={materialListTone(list.status)}>
          {formatMaterialListStatus(list.status)}
        </StatusBadge>
        {LINE_STATUSES.map((status) =>
          counts[status] ? (
            <span key={status}>
              {counts[status]}{" "}
              {formatMaterialListLineStatus(status).toLowerCase()}
            </span>
          ) : null,
        )}
        {list.archived_at && (
          <span>Archivée le {formatDate(list.archived_at)}</span>
        )}
      </div>

      {list.description && (
        <p className="text-[13px] text-ink-muted">{list.description}</p>
      )}

      <div className="min-w-0 overflow-hidden rounded-lg border border-border bg-surface">
        {lines.length === 0 ? (
          <EmptyState
            title="Liste vide"
            description="Ajoutez une ligne. Un article du catalogue, ou simplement ce que vous voulez, en toutes lettres."
          />
        ) : (
          <>
            <div className="hidden md:block">
              <TableShell>
                <thead>
                  <tr>
                    <Th className="max-w-0">Article</Th>
                    <Th align="right" className="w-32 whitespace-nowrap">
                      Quantité
                    </Th>
                    <Th className="hidden w-32 whitespace-nowrap @3xl:table-cell">
                      Priorité
                    </Th>
                    <Th className="hidden w-32 whitespace-nowrap @4xl:table-cell">
                      Requis pour
                    </Th>
                    <Th className="w-40">Statut</Th>
                    <Th className="w-12" />
                  </tr>
                </thead>
                <tbody>
                  {lines.map((line) => (
                    <LineRow
                      key={line.id}
                      listId={listId}
                      line={line}
                      editable={!closed}
                    />
                  ))}
                </tbody>
              </TableShell>
            </div>

            <ul className="divide-y divide-border/60 md:hidden">
              {lines.map((line) => (
                <li key={line.id}>
                  <LineCard listId={listId} line={line} editable={!closed} />
                </li>
              ))}
            </ul>
          </>
        )}
      </div>

      {!closed && (
        <div className="md:hidden">
          <Button
            variant="primary"
            className="w-full"
            onClick={() => setAdding(true)}
          >
            <Plus className="size-4" aria-hidden />
            Ajouter une ligne
          </Button>
        </div>
      )}

      <AddLineDialog open={adding} onOpenChange={setAdding} listId={listId} />
    </Page>
  );
}

/** The words somebody typed before a product was attached. */
function OriginalText({ line }: { line: MaterialListLine }) {
  if (!line.original_free_text || line.original_free_text === line.description)
    return null;
  return (
    <span className="block truncate text-[11px] text-ink-subtle">
      Saisi : « {line.original_free_text} »
    </span>
  );
}

function LineRow({
  listId,
  line,
  editable,
}: {
  listId: string;
  line: MaterialListLine;
  editable: boolean;
}) {
  const toast = useToast();
  const update = useUpdateMaterialListLine();
  const remove = useRemoveMaterialListLine();

  return (
    <Tr>
      <Td className="max-w-0">
        {line.product_id ? (
          <Link
            to={`/products/${line.product_id}`}
            className="block min-w-0 truncate font-medium text-ink hover:text-brand"
          >
            {line.description}
          </Link>
        ) : (
          <span className="block truncate font-medium text-ink">
            {line.description}
          </span>
        )}
        {line.product_part_number && (
          <span className="block truncate font-mono text-[11px] text-ink-subtle">
            {line.product_part_number}
          </span>
        )}
        {!line.product_id && (
          <span className="block text-[11px] text-ink-subtle">
            Hors catalogue
          </span>
        )}
        <OriginalText line={line} />
      </Td>
      <Td align="right" className="tnum whitespace-nowrap text-[13px]">
        {formatQuantity(line.quantity)} {formatUnit(line.unit)}
      </Td>
      <Td className="hidden whitespace-nowrap text-[13px] text-ink-muted @3xl:table-cell">
        {formatPlanningPriority(line.priority)}
      </Td>
      <Td className="hidden whitespace-nowrap text-[12px] text-ink-muted @4xl:table-cell">
        {formatDate(line.required_at)}
      </Td>
      <Td>
        {editable ? (
          <Select
            value={line.status}
            onChange={(event) =>
              update.mutate({
                listId,
                lineId: line.id,
                patch: { status: event.target.value as MaterialListLineStatus },
              })
            }
            aria-label={`Statut de ${line.description}`}
            className="h-9"
          >
            {LINE_STATUSES.map((status) => (
              <option key={status} value={status}>
                {formatMaterialListLineStatus(status)}
              </option>
            ))}
          </Select>
        ) : (
          <StatusBadge tone={materialListLineTone(line.status)}>
            {formatMaterialListLineStatus(line.status)}
          </StatusBadge>
        )}
      </Td>
      <Td>
        {editable && (
          <button
            type="button"
            aria-label={`Retirer ${line.description}`}
            onClick={() => {
              remove.mutate(
                { listId, lineId: line.id },
                {
                  onError: (error) =>
                    toast.error("Ligne non retirée", String(error)),
                },
              );
            }}
            className="rounded-md p-2 text-ink-subtle hover:text-danger"
          >
            <Trash2 className="size-4" aria-hidden />
          </button>
        )}
      </Td>
    </Tr>
  );
}

function LineCard({
  listId,
  line,
  editable,
}: {
  listId: string;
  line: MaterialListLine;
  editable: boolean;
}) {
  const update = useUpdateMaterialListLine();

  return (
    <div className="flex flex-col gap-2 px-3 py-3">
      <div className="min-w-0">
        <p className="truncate text-[14px] font-medium text-ink">
          {line.description}
        </p>
        <p className="text-[12px] text-ink-muted">
          {formatQuantity(line.quantity)} {formatUnit(line.unit)}
          {line.product_id ? "" : " · hors catalogue"}
        </p>
        <OriginalText line={line} />
      </div>
      {editable ? (
        <Select
          value={line.status}
          onChange={(event) =>
            update.mutate({
              listId,
              lineId: line.id,
              patch: { status: event.target.value as MaterialListLineStatus },
            })
          }
          aria-label={`Statut de ${line.description}`}
          className="h-11"
        >
          {LINE_STATUSES.map((status) => (
            <option key={status} value={status}>
              {formatMaterialListLineStatus(status)}
            </option>
          ))}
        </Select>
      ) : (
        <StatusBadge
          tone={materialListLineTone(line.status)}
          className="self-start"
        >
          {formatMaterialListLineStatus(line.status)}
        </StatusBadge>
      )}
    </div>
  );
}

/**
 * Add one line.
 *
 * Free text first, the catalogue second — deliberately. Someone standing in a
 * van types what they see; making them search a catalogue before they can note
 * anything is how a list stops being written at all.
 */
function AddLineDialog({
  open,
  onOpenChange,
  listId,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  listId: string;
}) {
  const toast = useToast();
  const addLine = useAddMaterialListLine();
  const [description, setDescription] = useState("");
  const [term, setTerm] = useState("");
  const [productId, setProductId] = useState("");
  const [quantity, setQuantity] = useState("1");
  const [unit, setUnit] = useState<UnitCode>("EA");
  const [priority, setPriority] = useState<PlanningPriority>("NORMAL");

  const { data: products } = useInventory(
    { q: term.trim() || undefined, limit: 25 },
    { enabled: open && term.trim().length > 1 },
  );

  function reset() {
    setDescription("");
    setTerm("");
    setProductId("");
    setQuantity("1");
    setUnit("EA");
    setPriority("NORMAL");
  }

  async function submit() {
    if (!quantity || (!description.trim() && !productId)) return;
    try {
      await addLine.mutateAsync({
        listId,
        description: description.trim() || null,
        product_id: productId || null,
        quantity,
        unit: productId ? null : unit,
        priority,
      });
      toast.success("Ligne ajoutée");
      reset();
      onOpenChange(false);
    } catch (error) {
      toast.error("Ligne non ajoutée", String(error));
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) reset();
        onOpenChange(next);
      }}
      title="Ajouter une ligne"
      description="Écrivez ce qu'il vous faut. L'article du catalogue est facultatif."
      footer={
        <>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            Annuler
          </Button>
          <Button
            variant="primary"
            onClick={() => void submit()}
            disabled={
              (!description.trim() && !productId) ||
              !quantity ||
              addLine.isPending
            }
          >
            {addLine.isPending ? "Ajout…" : "Ajouter"}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <Field
          label="Ce qu'il vous faut"
          hint="En toutes lettres. « boite inox speciale environ 12x12 » est une ligne valide."
        >
          <Input
            value={description}
            onChange={(event) => setDescription(event.target.value)}
            placeholder="Description"
            aria-label="Description de la ligne"
          />
        </Field>

        <Field label="Article du catalogue" hint="Facultatif.">
          <div className="relative">
            <Search
              className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-ink-subtle"
              aria-hidden
            />
            <Input
              value={term}
              onChange={(event) => setTerm(event.target.value)}
              placeholder="Nom, fabricant, MPN…"
              className="pl-9"
              aria-label="Chercher un article"
            />
          </div>
          <Select
            value={productId}
            onChange={(event) => setProductId(event.target.value)}
            aria-label="Article du catalogue"
            className="mt-2"
          >
            <option value="">Aucun — ligne libre</option>
            {products?.items.map((row) => (
              <option key={row.product_id} value={row.product_id}>
                {row.name}
                {row.part_number ? ` (${row.part_number})` : ""}
              </option>
            ))}
          </Select>
        </Field>

        <div className="flex gap-3">
          <div className="flex-1">
            <Field label="Quantité">
              <Input
                type="number"
                min="0"
                step="any"
                inputMode="decimal"
                value={quantity}
                onChange={(event) => setQuantity(event.target.value)}
                aria-label="Quantité"
              />
            </Field>
          </div>
          {!productId && (
            <div className="w-32">
              <Field label="Unité">
                <Select
                  value={unit}
                  onChange={(event) => setUnit(event.target.value as UnitCode)}
                  aria-label="Unité"
                >
                  {STOCKABLE_UNITS.map((code) => (
                    <option key={code} value={code}>
                      {formatUnit(code)}
                    </option>
                  ))}
                </Select>
              </Field>
            </div>
          )}
        </div>

        <Field label="Priorité">
          <Select
            value={priority}
            onChange={(event) =>
              setPriority(event.target.value as PlanningPriority)
            }
            aria-label="Priorité"
          >
            {PRIORITIES.map((value) => (
              <option key={value} value={value}>
                {formatPlanningPriority(value)}
              </option>
            ))}
          </Select>
        </Field>
      </div>
    </Dialog>
  );
}
